import './greenhouse.scss';

import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import Dropdown from 'react-dropdown-select';

import dropdownArrow from '../../../static/drop-down-arrow.png';
import { AlmaLink } from '../alma-link';
import * as styles from './greenhouse.module.css';

function formatRawJobData(rawJobData, jobTitle) {
	// remove all text between &lt; and &gt; (inclusive)
	// this removes all html tags
	const jobTextContent = rawJobData.replace(/&lt;.*?&gt;/g, '');

	// only keep text after Interview Guide and before What you’ll do
	const trimmedJobTextContent = jobTextContent.substring(
		jobTextContent.indexOf('Interview Guide') + 15,
		jobTextContent.indexOf('What you’ll do')
	);

	// remove first instance of job title from text
	const trimmedJobTextContentNoJobTitle = trimmedJobTextContent.replace(
		jobTitle,
		''
	);

	// replace other non wanted characters
	const formattedJobDescription = trimmedJobTextContentNoJobTitle
		.replace(/&amp;nbsp;&amp;nbsp;/g, '')
		.replace(/&amp;nbsp;/g, '')
		.replace(/&#39;/g, "'")
		.replace(/&amp;amp;/g, '&');

	return formattedJobDescription;
}

export function Greenhouse({ cta, title }) {
	// state
	const [departments, setDepartments] = useState([]);
	const [selectedDepartment, setSelectedDepartment] = useState();

	// The reason we load data on module load is we want to show the most up to date jobs data from Greenhouse
	// Previously this was handled during the page build process via `gatsby-node`, but this was causing issues with stale data
	useEffect(() => {
		const fetchGreenhouseData = async () => {
			const greenhouseResponse = await fetch(
				'https://boards-api.greenhouse.io/v1/boards/alma/jobs?content=true'
			);

			const greenhouseData = await greenhouseResponse.json();
			const allJobsData = greenhouseData?.jobs;

			// reduce jobsData, grouping by department, sort by number of jobs in each department
			const formattedJobData = allJobsData
				.reduce((acc, job) => {
					const department = job.departments[0];
					const departmentName = department.name;
					const departmentId = department.id;

					const departmentIndex = acc.findIndex(
						(dept) => dept.name === departmentName
					);
					if (departmentIndex === -1) {
						acc.push({
							id: departmentId,
							name: departmentName,
							jobs: [job],
						});
					} else {
						acc[departmentIndex].jobs.push(job);
					}
					return acc;
				}, [])
				.sort((a, b) => b.jobs.length - a.jobs.length);

			setDepartments(formattedJobData);
		};

		fetchGreenhouseData();
	}, []);

	useEffect(() => {
		if (departments.length > 0) {
			setSelectedDepartment(departments[0].id);
		}
	}, [departments]);

	const faqClickHandler = (jobId) => {
		const jobDescription = document.getElementById(`${jobId}-description`);
		const hideLink = document.getElementById(`${jobId}-details`);

		if (jobDescription.style.maxHeight) {
			jobDescription.style.maxHeight = null;
			hideLink.innerHTML = 'Show details';
		} else {
			jobDescription.style.maxHeight = jobDescription.scrollHeight + 'px';
			hideLink.innerHTML = 'Hide details';
		}
	};

	const selectPreviousDepartment = () => {
		// check if selectedDepartment is the first in list of departments
		const currentDepartmentIndex = departments.findIndex(
			(department) => department.id === selectedDepartment
		);
		const previousDepartment = departments[currentDepartmentIndex - 1];

		if (previousDepartment) {
			setSelectedDepartment(previousDepartment.id);
		}

		// scroll department-names to the left by the width of the previous department name
		const departmentNames = document.querySelector('.department-names');
		const previousDepartmentName = document.querySelector(
			'.department-name.selected'
		).previousSibling;

		if (previousDepartmentName) {
			departmentNames.scrollLeft -= previousDepartmentName.clientWidth;
		}
	};

	const selectNextDepartment = () => {
		const currentDepartmentIndex = departments.findIndex(
			(department) => department.id === selectedDepartment
		);
		const nextDepartment = departments[currentDepartmentIndex + 1];

		if (nextDepartment) {
			setSelectedDepartment(nextDepartment.id);
		}

		// scroll department-names to the right by the width of the next department name
		const departmentNames = document.querySelector('.department-names');
		const nextDepartmentName = document.querySelector(
			'.department-name.selected'
		).nextSibling;

		if (nextDepartmentName) {
			departmentNames.scrollLeft += nextDepartmentName.clientWidth;
		}
	};

	return (
		<div
			className={classNames(
				styles.greenhouseContainer,
				'responsive-layout',
				'module-padding-h',
				'module-padding-v-xl',
				'bcpgr'
			)}
			id='open-roles'
		>
			<div className={styles.greenhouseHeaderContainer}>
				{title && <h2 className='heading-l'>{title}</h2>}
				{cta && <AlmaLink cta={cta} />}
			</div>
			<div className={classNames(styles.greenhouseJobsContainer)}>
				<div className='departments-nav-container'>
					<p className='departments-nav-eyebrow'>Find roles in:</p>
					<div className={styles.departmentsContainer}>
						{/* desktop departments nav */}
						<div className={styles.manualNavContainer}>
							<button
								className={styles.manualNavButtonLeft}
								onClick={selectPreviousDepartment}
							>
								<img src='/arrow-right.svg' alt='arrow left' />
							</button>
							<div className={styles.manualNavButtonBlurLeft} />
						</div>
						<div className='department-names' role='menu'>
							{departments?.map((department) => {
								return (
									<div
										tabIndex={0}
										role='menuitem'
										className={`department-name rel ${
											selectedDepartment === department.id
												? 'selected'
												: ''
										} pointer`}
										onClick={() =>
											setSelectedDepartment(department.id)
										}
										key={department.id}
									>
										<p className='heading-xs cblg'>
											{department.name} (
											{department.jobs.length})
										</p>
									</div>
								);
							})}
						</div>
						<div className={styles.manualNavContainer}>
							<button
								className={styles.manualNavButtonRight}
								onClick={selectNextDepartment}
							>
								<img src='/arrow-left.svg' alt='arrow left' />
							</button>
							<div className={styles.manualNavButtonBlurRight} />
						</div>
					</div>
					{/* mobile dropdown */}
					<div className='field-group mobile-dropdown department-names-mobile'>
						<div className='field-group__inner rel'>
							<img
								className='abs'
								width='20px'
								height='20px'
								src={dropdownArrow}
								style={{
									bottom: '15px',
									right: '10px',
									zIndex: '1',
									opacity: '0.3',
								}}
								alt='dropdown arrow'
							/>
							{departments.length > 0 && (
								<Dropdown
									dropdownHandle={false}
									onChange={(option) =>
										setSelectedDepartment(option[0].value)
									}
									options={departments.map((department) => ({
										value: department.id,
										label: `${department.name} (${department.jobs.length})`,
									}))}
									value={
										departments.map((department) => ({
											value: department.id,
											label: `${department.name} (${department.jobs.length})`,
										}))[0]
									}
									defaultValue={
										departments.map((department) => ({
											value: department.id,
											label: `${department.name} (${department.jobs.length})`,
										}))[0]
									}
									values={[
										departments
											.map((department) => ({
												value: department.id,
												label: `${department.name} (${department.jobs.length})`,
											}))
											.find(
												(opt, idx) =>
													idx === 0 && opt.value
											),
									]}
								/>
							)}
						</div>
					</div>
				</div>
				<div>
					{departments.map((department) => {
						if (selectedDepartment === department.id) {
							return (
								<div
									className={classNames(
										'department-body f fdc',
										selectedDepartment === department.id &&
											'selected'
									)}
									id={department.id}
									key={department.id}
								>
									{department.jobs.map((job) => {
										const formattedJobDescription =
											formatRawJobData(
												job?.content,
												job?.title
											);

										return (
											<div
												className={styles.jobContainer}
												key={job?.id}
											>
												<div className='job-item'>
													<div>
														<h3 className='body-display-sm'>
															{job?.title}
														</h3>
													</div>
													<div
														className='jobDescriptionContainer'
														id={`${job?.id}-description`}
													>
														<p
															className={
																styles.jobDescriptionText
															}
														>
															{
																formattedJobDescription
															}
														</p>
													</div>
													<button
														className='show-link'
														onClick={() =>
															faqClickHandler(
																job?.id
															)
														}
														id={`${job?.id}-details`}
													>
														Show details
													</button>
												</div>
												<a
													className={
														styles.jobApplyButton
													}
													href={job?.absolute_url}
													target='_blank'
													rel='noopener noreferrer'
												>
													Apply
												</a>
											</div>
										);
									})}
								</div>
							);
						}
					})}
				</div>
			</div>
		</div>
	);
}
